





















































































































import { Component, Vue, Prop } from 'vue-property-decorator';
import { GQL_newInvoiceData_newInvoice } from '@/queries/__generated__/newInvoiceData';
import InvoiceMailButton from './InvoiceMailButton.vue';
import InvoiceSendByEHFButton from './InvoiceSendByEHFButton.vue';
import InvoiceDeliveryButton from './InvoiceDeliveryButton.vue';
import { GET_CUSTOMER, UPDATE_CUSTOMER } from '@/queries/Customer';
import { UNIT_INVOICE_SETTINGS } from '@/queries/CompanySetting';
import { SmartQuery } from 'vue-apollo-decorator';
import { GQL_customer, GQL_customer_customer } from '@/queries/__generated__/customer';
import { GQL_unitInvoiceSetting, GQL_unitInvoiceSetting_currentUnit_unitSetting } from '@/queries/__generated__/unitInvoiceSetting';
import { GQL_InvoiceDeliveryMethodEnum } from '@/queries/global';
import i18n from '@/i18n';

@Component({
  components: {
    InvoiceMailButton,
    InvoiceSendByEHFButton,
    InvoiceDeliveryButton,
  },
})
export default class NewInvoiceDeliveryDialog extends Vue {
  @Prop(Object) public invoice!: GQL_newInvoiceData_newInvoice;

  private open = false;

  @SmartQuery<NewInvoiceDeliveryDialog, GQL_customer>({
    query: () => GET_CUSTOMER,
    variables() {
      return {
        id: this.invoice.customer.id,
      };
    },
    result({ data }) {
      if (!data.customer) return;

      this.onCustomerLoaded(data.customer);
    },
  }) public customer!: GQL_customer_customer;

  @SmartQuery<NewInvoiceDeliveryDialog, GQL_unitInvoiceSetting>({
    query: () => UNIT_INVOICE_SETTINGS,
    update: (data) => data.currentUnit.unitSetting,
  }) public unitSetting!: GQL_unitInvoiceSetting_currentUnit_unitSetting;

  private selectedMethod: GQL_InvoiceDeliveryMethodEnum | null = null;
  private isEditingMethod = false;
  private decided = false;

  // The customer loads asynchronously, so decide whether to prompt once it
  // arrives.
  //
  // The decision is made only once. Both mutations below return the whole
  // customer, so each one rewrites the cached record and the query re-emits —
  // and if the user changed the delivery method and closed the dialog before
  // that response landed, reopening it here would be the wrong answer.
  private onCustomerLoaded(customer: GQL_customer_customer) {
    this.selectedMethod = customer.invoiceDeliveryMethod;

    if (this.decided) return;
    this.decided = true;

    // "manual" customers receive invoices directly on this platform, so there
    // is nothing to send and no reason to prompt.
    if (customer.invoiceDeliveryMethod === GQL_InvoiceDeliveryMethodEnum.manual) return;

    // opted-in customers are delivered server side by SendInvoiceAutomaticaly
    // when the invoice is created, so there is nothing to prompt for.
    if (customer.autoSendInvoice) return;

    this.open = true;
  }

  private get initialMailDestination() {
    return `${this.customer.unit.address}, ${this.customer.unit.zip}, ${this.customer.unit.city}`;
  }

  private get isManualSelected() {
    return this.selectedMethod === GQL_InvoiceDeliveryMethodEnum.manual;
  }

  private buttonColor(method: GQL_InvoiceDeliveryMethodEnum) {
    return this.selectedMethod === method ? 'primary' : 'standard';
  }

  private updateDeliveryMethod(method: GQL_InvoiceDeliveryMethodEnum) {
    if (method === this.selectedMethod) return;

    const previousMethod = this.selectedMethod;
    const isManual = method === GQL_InvoiceDeliveryMethodEnum.manual;
    this.selectedMethod = method;

    this.$apollo.mutate({
      mutation: UPDATE_CUSTOMER,
      variables: {
        customer: {
          id: this.customer.id,
          invoiceDeliveryMethod: method,
          // "never deliver" and "deliver every future invoice automatically"
          // contradict each other, and SendInvoiceAutomaticaly has no branch
          // for "manual" — it would pass its opted-in guard and then quietly
          // deliver nothing. Turning auto delivery off keeps that unreachable.
          ...(isManual ? {autoSendInvoice: false} : {}),
        },
      },
    }).then(() => {
      this.$sMessage.success(i18n.t('Message.saveSuccess'));

      // "manual" means this customer is never delivered to, so there is nothing
      // left to send and no reason to keep asking. The customer query re-emits
      // on the mutation response, but onCustomerLoaded has already decided, so
      // the dialog has to be closed here.
      if (isManual) this.open = false;
    }).catch(() => {
      // the errors link reports the failure; undo the optimistic selection so
      // the radio does not claim a preference that was never saved
      this.selectedMethod = previousMethod;
    });
  }

  private enableAutoSend() {
    this.$apollo.mutate({
      mutation: UPDATE_CUSTOMER,
      variables: {
        customer: {
          id: this.customer.id,
          autoSendInvoice: true,
        },
      },
    }).then(() => {
      // only stores the preference for future invoices — keep the dialog open
      // so the user can still deliver the current invoice
      this.$sMessage.success(i18n.t('Message.saveSuccess'));
    });
  }

  private onSent() {
    this.open = false;
  }
}
